import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, Download, ArrowLeft, Calendar, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LetterPage = () => {
  const { t } = useLanguage();
  const { letterId } = useParams();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [patientEmail, setPatientEmail] = useState("");

  const fetchLetter = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/letters/${letterId}`);
      setLetter(response.data);
      
      // Get email from sessionStorage or letter
      const storedEmail = sessionStorage.getItem('patientEmail');
      const letterEmail = response.data.patient_email;
      const emailToUse = storedEmail || letterEmail;
      setPatientEmail(emailToUse);
      
      // Check payment status
      if (emailToUse) {
        const statusResponse = await axios.get(`${API}/letters/patient/${encodeURIComponent(emailToUse)}`);
        setHasPaid(statusResponse.data.has_paid || false);
      }
    } catch (error) {
      console.error("Error fetching letter:", error);
      toast.error("Lettera non trovata");
    } finally {
      setLoading(false);
    }
  }, [letterId]);

  useEffect(() => {
    if (letterId) {
      fetchLetter();
    }
  }, [letterId, fetchLetter]);

  const handleDownloadPDF = async () => {
    if (!letter || !hasPaid) return;
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/letters/${letterId}/pdf?email=${encodeURIComponent(patientEmail)}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_${letter.patient_name.replace(/\s/g, '_')}_${letter.month.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF scaricato con successo!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      if (error.response?.status === 403) {
        toast.error("Devi avere un abbonamento attivo per scaricare le lettere");
      } else {
        toast.error("Errore nel download del PDF");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="letter-page">
        <Header />
        <main className="pt-32 pb-24 px-6">
          <div className="max-w-3xl mx-auto flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="letter-page">
        <Header />
        <main className="pt-32 pb-24 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-stone-900 mb-4">{t("common.notFound")}</h1>
            <Link to="/">
              <Button variant="outline" className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("common.backHome")}
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="letter-page">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/le-mie-lettere" 
            className="inline-flex items-center gap-2 text-[#8a9a86] hover:text-[#2c4c3b] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("letters.backToLetters")}
          </Link>

          {/* Letter Card */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-stone-100 overflow-hidden">
            {/* Header */}
            <div className="bg-[#2c4c3b] px-8 py-10 text-center">
              <h1 className="font-serif text-3xl text-stone-100 mb-2">
                Phisioletters
              </h1>
              <p className="text-[#8a9a86] text-sm">Mail Club - Fisioterapia Olistica</p>
            </div>

            {/* Letter Content */}
            <div className="p-8 md:p-12">
              {/* Title and date */}
              <div className="text-center mb-10">
                <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-3">
                  {letter.title}
                </h2>
                <div className="flex items-center justify-center gap-2 text-[#8a9a86]">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{letter.month}</span>
                </div>
              </div>

              {/* Greeting */}
              <p className="text-[#2c4c3b] font-medium text-lg mb-6">
                {t("letters.dear")} {letter.patient_name},
              </p>

              {/* Content */}
              <div className="prose prose-stone max-w-none">
                {letter.content.split('\n\n').map((paragraph, idx) => (
                  <p key={`para-${idx}`} className="text-stone-600 leading-relaxed mb-4 whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Signature */}
              <div className="mt-10 pt-8 border-t border-stone-100">
                <p className="text-stone-600 italic mb-2">{t("letters.withAffection")}</p>
                <p className="font-serif text-xl text-[#2c4c3b]">Marlene Brochero</p>
                <p className="text-sm text-[#8a9a86]">Fisioterapista & Operatore Olistico</p>
              </div>
            </div>

            {/* Download Section */}
            <div className="px-8 pb-8 md:px-12 md:pb-12">
              {hasPaid ? (
                <>
                  <Button
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6 text-base group"
                    data-testid="download-pdf-btn"
                  >
                    {downloading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2 group-hover:-translate-y-0.5 transition-transform" />
                        {t("letters.downloadPdf")}
                      </>
                    )}
                  </Button>
                  <p className="text-center text-xs text-stone-400 mt-3">
                    {t("letters.pdfNote")}
                  </p>
                </>
              ) : (
                <div className="bg-stone-50 rounded-2xl p-6 text-center">
                  <Lock className="w-10 h-10 text-stone-300 mx-auto mb-4" />
                  <h3 className="font-medium text-stone-900 mb-2">{t("letters.downloadReserved")}</h3>
                  <p className="text-stone-500 text-sm mb-4">
                    {t("letters.downloadReservedText")}
                  </p>
                  <Link to="/pricing">
                    <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full">
                      {t("letters.discoverPlans")}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LetterPage;
